import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Grow,
  Zoom,
  Fab,
  Snackbar,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Agriculture,
  Security,
  People,
  Analytics,
  Settings,
  Wifi,
  Sensors,
  CameraAlt,
  QrCode,
  AccessTime,
  TrendingUp,
  Cloud,
  Build,
  Speed,
  CheckCircle,
  Close,
  Send,
  Email,
  Phone,
  LocationOn,
  ArrowForward,
  PlayArrow,
  Star,
  VerifiedUser,
  Support,
  Dashboard,
  Automation,
  Monitoring,
  SmartToy,
  Psychology,
  Lightbulb,
  Eco,
  Science,
  Engineering,
  Business,
  School,
  Work,
  Home,
  Apartment,
  Factory,
  Warehouse,
  Store,
  Storefront,
  LocalShipping,
  Inventory,
  Assessment,
  Timeline,
  BarChart,
  PieChart,
  ShowChart,
  InsertChart,
  DataUsage,
  Memory,
  Storage,
  StorageOutlined,
  CloudDone,
  CloudSync,
  CloudUpload,
  CloudDownload,
  CloudOff,
  CloudQueue,
  CloudCircle,
  WbSunny,
  Opacity,
  Air,
  Thermostat,
  AcUnit,
  LocalFireDepartment,
  WaterDrop,
  EcoOutlined,
  Nature,
  Park,
  Forest,
  Grass,
  Yard,
  Garden,
  LocalFlorist,
  Spa,
  Landscape,
  Terrain,
  Public,
  LocationCity,
  BusinessCenter,
  Domain,
  AccountBalance,
  AccountBalanceWallet,
  MonetizationOn,
  AttachMoney,
  Euro,
  CurrencyExchange,
  TrendingDown,
  TrendingFlat,
  TrendingUpOutlined,
  TrendingDownOutlined,
  TrendingFlatOutlined,
  TrendingUpRounded,
  TrendingDownRounded,
  TrendingFlatRounded,
  TrendingUpSharp,
  TrendingDownSharp,
  TrendingFlatSharp,
  TrendingUpTwoTone,
  TrendingDownTwoTone,
  TrendingFlatTwoTone,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { sendContactEmail, validateContactForm } from '../services/emailService';

// Styled Components
const Header = styled(AppBar)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(20px)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: 'none',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
}));

const HeaderLogo = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 900,
  fontSize: '1.8rem',
  cursor: 'pointer',
}));

const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  paddingTop: '80px', // Add padding for header
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3,
  },
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 900,
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(255, 255, 255, 0.3)'}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.primary.main,
  },
}));

const FloatingActionButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  zIndex: 1000,
  
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    transform: 'scale(1.1)',
  },
}));

const ContactForm = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(255, 255, 255, 0.3)'}`,
}));

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formErrors, setFormErrors] = useState({});

  const features = [
    {
      icon: <Agriculture sx={{ fontSize: 40 }} />,
      title: 'Indoor Cultivation Management',
      description: 'Complete control over your indoor growing environment with AI-powered optimization.',
      color: '#4caf50',
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Advanced Security System',
      description: 'Comprehensive security with UniFi integration, access control, and surveillance.',
      color: '#f44336',
    },
    {
      icon: <People sx={{ fontSize: 40 }} />,
      title: 'Employee Management',
      description: 'Streamlined employee tracking, access control, and performance monitoring.',
      color: '#2196f3',
    },
    {
      icon: <Analytics sx={{ fontSize: 40 }} />,
      title: 'Real-time Analytics',
      description: 'AI-driven insights and predictive analytics for optimal facility performance.',
      color: '#ff9800',
    },
    {
      icon: <Settings sx={{ fontSize: 40 }} />,
      title: 'Automated Control Systems',
      description: 'Smart automation for climate control, lighting, and environmental monitoring.',
      color: '#9c27b0',
    },
    {
      icon: <Wifi sx={{ fontSize: 40 }} />,
      title: 'IoT Integration',
      description: 'Seamless integration with sensors, cameras, and smart devices throughout your facility.',
      color: '#00bcd4',
    },
  ];

  const benefits = [
    'Increase crop yields by up to 40%',
    'Reduce energy consumption by 30%',
    'Automate routine tasks and monitoring',
    'Enhance security and access control',
    'Real-time data and predictive analytics',
    'Scalable solution for any facility size',
    '24/7 monitoring and alerting',
    'Compliance and reporting tools',
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormErrors({});
    
    try {
      const validationResult = validateContactForm(contactForm);
      if (!validationResult.isValid) {
        setFormErrors(validationResult.errors);
        setSnackbar({
          open: true,
          message: 'Please fix the errors in the form.',
          severity: 'error'
        });
        return;
      }
      
      const result = await sendContactEmail(contactForm);
      
      setSnackbar({
        open: true,
        message: result.message || 'Thank you! We\'ll contact you soon to schedule your demo.',
        severity: 'success'
      });
      
      setContactDialogOpen(false);
      setContactForm({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Something went wrong. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setContactForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Header */}
      <Header>
        <Toolbar>
          <HeaderLogo onClick={() => navigate('/')}>
            HarvestAI
          </HeaderLogo>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2 }}>
            <Button
              color="inherit"
              onClick={() => navigate('/login')}
              sx={{
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Login
            </Button>
            <Button
              variant="contained"
              onClick={() => setContactDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                },
              }}
            >
              Get Demo
            </Button>
          </Box>
          <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setContactDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                },
              }}
            >
              Demo
            </Button>
          </Box>
        </Toolbar>
      </Header>

      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip
                    label="AI-Powered Innovation"
                    color="primary"
                    sx={{ 
                      mb: 2, 
                      fontWeight: 600,
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  />
                  <GradientText variant="h2" component="h1" sx={{ 
                    fontWeight: 900, 
                    mb: 2,
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    lineHeight: 1.1,
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  }}>
                    Revolutionize Your Indoor Cultivation
                  </GradientText>
                  <Typography variant="h5" sx={{ 
                    mb: 4, 
                    color: 'white',
                    fontWeight: 500,
                    lineHeight: 1.4,
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                  }}>
                    Harness the power of AI to control every aspect of your indoor harvest facility. 
                    From climate control to employee management, security to analytics - everything 
                    you need in one intelligent platform.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/login')}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        textTransform: 'none',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Get Started
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => setContactDialogOpen(true)}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: 'white',
                          background: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      Request Demo
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Slide direction="left" in timeout={1500}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 4,
                      p: 3,
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <Typography variant="h4" sx={{ 
                      color: 'white', 
                      mb: 2, 
                      fontWeight: 700,
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                    }}>
                      🚀 Powered by AI
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      color: 'white', 
                      mb: 3,
                      fontWeight: 500,
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                    }}>
                      Experience the future of indoor cultivation management
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Chip 
                        icon={<SmartToy />} 
                        label="AI Automation" 
                        sx={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          fontWeight: 600,
                        }}
                      />
                      <Chip 
                        icon={<Psychology />} 
                        label="Smart Analytics" 
                        sx={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          fontWeight: 600,
                        }}
                      />
                      <Chip 
                        icon={<Lightbulb />} 
                        label="Intelligent Control" 
                        sx={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Features Section */}
      <Box sx={{ py: 8, background: theme.palette.background.default }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" sx={{ 
              fontWeight: 700, 
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Everything You Need
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Comprehensive indoor cultivation management system with cutting-edge AI technology
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Grow in timeout={1000 + index * 200}>
                  <FeatureCard>
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                      <Box sx={{ 
                        color: feature.color, 
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'center',
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" component="h3" sx={{ 
                        fontWeight: 600, 
                        mb: 2,
                        color: theme.palette.text.primary,
                      }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ 
        py: 8, 
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Typography variant="h3" component="h2" sx={{ 
                    fontWeight: 700, 
                    mb: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}>
                    Why Choose HarvestAI?
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                    Transform your indoor cultivation facility with AI-powered intelligence
                  </Typography>
                  <List>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CheckCircle sx={{ color: '#4caf50' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={benefit}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Zoom in timeout={1500}>
                <Box
                  sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4,
                    p: 4,
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'primary.main', mb: 2, fontWeight: 700 }}>
                    🎯 Results That Matter
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Box>
                      <Typography variant="h3" sx={{ color: '#4caf50', fontWeight: 900 }}>
                        40%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Yield Increase
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ color: '#ff9800', fontWeight: 900 }}>
                        30%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Energy Savings
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ color: '#2196f3', fontWeight: 900 }}>
                        24/7
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Monitoring
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ color: '#9c27b0', fontWeight: 900 }}>
                        AI
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Powered
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ 
        py: 8, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        textAlign: 'center',
      }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 700, 
            mb: 3,
            color: 'white',
          }}>
            Ready to Transform Your Facility?
          </Typography>
          <Typography variant="h6" sx={{ 
            mb: 4,
            color: 'rgba(255, 255, 255, 0.9)',
          }}>
            Join the future of indoor cultivation management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                background: 'white',
                color: '#667eea',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                textTransform: 'none',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.9)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => setContactDialogOpen(true)}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Schedule Demo
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ 
        py: 4, 
        background: theme.palette.mode === 'dark' ? '#0f0f23' : '#f5f5f5',
        borderTop: `1px solid ${theme.palette.divider}`,
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  HarvestAI
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Revolutionary indoor cultivation management system powered by AI
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/login')}
                  sx={{ textTransform: 'none' }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setContactDialogOpen(true)}
                  sx={{ 
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  Contact Us
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ 
            mt: 3, 
            pt: 3, 
            borderTop: `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
          }}>
            <Typography variant="body2" color="text.secondary">
              © 2024 HarvestAI. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Dialog */}
      <Dialog 
        open={contactDialogOpen} 
        onClose={() => setContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'white',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontWeight: 700,
          fontSize: '1.5rem',
          pb: 1,
        }}>
          Request a Demo
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <ContactForm component="form" onSubmit={handleContactSubmit} sx={{ 
            mt: 2,
            background: 'white',
            borderRadius: 2,
            p: 0,
            border: 'none',
          }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={contactForm.name}
                  onChange={handleInputChange('name')}
                  required
                  variant="outlined"
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={contactForm.email}
                  onChange={handleInputChange('email')}
                  required
                  variant="outlined"
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company"
                  value={contactForm.company}
                  onChange={handleInputChange('company')}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={contactForm.phone}
                  onChange={handleInputChange('phone')}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={contactForm.message}
                  onChange={handleInputChange('message')}
                  placeholder="Tell us about your facility and requirements..."
                  variant="outlined"
                  required
                  error={!!formErrors.message}
                  helperText={formErrors.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </ContactForm>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setContactDialogOpen(false)}
            sx={{ 
              textTransform: 'none',
              color: 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleContactSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            {loading ? 'Sending...' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <FloatingActionButton
        color="primary"
        onClick={() => setContactDialogOpen(true)}
        aria-label="contact"
      >
        <Email />
      </FloatingActionButton>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LandingPage; 